"use client";

import { useCallback, useRef, useState } from "react";
import { submitJob, getJobStatus, CreatorVoiceError } from "@/lib/creatorVoice/api";
import { watchJob, type JobDoc } from "@/lib/platform/firebase";

const POLL_INTERVAL_MS = 2500;

export function useJob() {
  const [job, setJob] = useState<JobDoc | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [capped, setCapped] = useState(false);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const run = useCallback(async (type: string, payload: Record<string, unknown>) => {
    unsubscribeRef.current?.();
    if (pollRef.current) clearInterval(pollRef.current);
    setError(null);
    setCapped(false);
    setJob(null);
    setSubmitting(true);
    try {
      const jobId = await submitJob(type, payload);
      setJob({ status: "pending" });

      const handleUpdate = (updated: JobDoc) => {
        setJob(updated);
        if (updated.status === "failed") setError(updated.error || "Job failed.");
        if (updated.status === "completed" || updated.status === "failed") {
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        }
      };

      unsubscribeRef.current = watchJob(jobId, handleUpdate);

      // Fallback poll via the backend's own HTTP API (not Firestore) in
      // case the realtime listener's push never arrives — some networks
      // silently break Firestore's own channel/REST access for end users.
      pollRef.current = setInterval(async () => {
        const polled = await getJobStatus(jobId);
        if (polled) handleUpdate(polled);
      }, POLL_INTERVAL_MS);
    } catch (e) {
      if (e instanceof CreatorVoiceError && e.status === 402) {
        setCapped(true);
        setError(e.message);
      } else {
        setError(e instanceof CreatorVoiceError ? e.message : "Something went wrong.");
      }
    } finally {
      setSubmitting(false);
    }
  }, []);

  const reset = useCallback(() => {
    unsubscribeRef.current?.();
    if (pollRef.current) clearInterval(pollRef.current);
    setJob(null);
    setError(null);
    setCapped(false);
  }, []);

  return { job, submitting, error, capped, run, reset };
}
